import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Building2, Briefcase, Search, MapPin, BadgeCheck, ArrowRight, ArrowLeft } from 'lucide-react';

const C = { orange: '#E08B3C', teal: '#1F8FB8', green: '#1E9E47' };
const LOGO_WORDMARK = "https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/6tkmr7v8_3.png";

export default function Catalog() {
    const { type = 'client' } = useParams();
    const isClient = type === 'client';
    const accent = isClient ? C.teal : C.orange;
    const Icon = isClient ? Building2 : Briefcase;
    const title = isClient ? 'Clientes principales con Distintivo' : 'Proveedores con Distintivo';
    const subtitle = isClient
        ? 'Negocios con distintivo vigente otorgado por AIGIS'
        : 'Proveedores con distintivo vigente otorgado por AIGIS';

    const [entries, setEntries] = useState([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/catalog?type=${type}`).then((r) => setEntries(r.data)).finally(() => setLoading(false));
    }, [type]);

    const filtered = entries.filter((e) =>
        !query || e.name.toLowerCase().includes(query.toLowerCase()) ||
        e.industry.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-white dark:bg-[#0A0A0A] text-[#0A0A0A] dark:text-white">
            <header className="border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center">
                        <img src={LOGO_WORDMARK} alt="AIGIS CERTIFICATE" className="h-14 w-auto object-contain" />
                    </Link>
                    <Link to="/" className="inline-flex items-center gap-2 border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5">
                        <ArrowLeft className="w-4 h-4" /> Volver al inicio
                    </Link>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 py-12">
                <div className="flex items-start gap-5 mb-8">
                    <div className="w-16 h-16 flex items-center justify-center" style={{ background: accent }}>
                        <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight">{title}</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>
                    </div>
                </div>

                <div className="border border-gray-200 dark:border-gray-800 flex items-center gap-3 px-4 py-3 mb-6 bg-white dark:bg-[#141414]">
                    <Search className="w-5 h-5 text-gray-400" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar por nombre o giro..."
                        data-testid="catalog-search"
                        className="flex-1 bg-transparent outline-none border-0 px-0 py-0 text-sm"
                    />
                </div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-6">
                    {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
                </div>

                {loading ? (
                    <div className="p-12 text-center text-gray-500">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500">
                        Sin empresas con distintivo aún en esta categoría.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {filtered.map((e) => (
                            <Link
                                key={e.id}
                                to={`/catalogo/empresa/${e.id}`}
                                data-testid={`catalog-card-${e.id}`}
                                className="block border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#141414] hover:border-[#0A0A0A] dark:hover:border-white transition-colors group"
                            >
                                <div className="relative h-44 bg-gray-100 dark:bg-[#1F1F1F]">
                                    {e.image_url ? (
                                        <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Icon className="w-10 h-10 text-gray-400" />
                                        </div>
                                    )}
                                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-white/95 border" style={{ color: C.green, borderColor: C.green }}>
                                        <BadgeCheck className="w-3 h-3" /> Verificado
                                    </span>
                                </div>
                                <div className="p-5">
                                    <div className="font-display text-base font-bold mb-2 leading-tight">{e.name}</div>
                                    <div className="inline-block text-[11px] font-medium px-2 py-1 mb-2" style={{ background: `${accent}22`, color: accent }}>
                                        {e.industry}
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                                        <MapPin className="w-3 h-3" /> {e.location}
                                    </div>
                                    <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-100 dark:border-gray-800">
                                        <span className="font-bold" style={{ color: C.green }}>◆ CERTIFICADO</span>
                                        <span className="font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                                            Ver perfil <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export function CatalogDetail() {
    const { id } = useParams();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/catalog/${id}`).then((r) => setEntry(r.data)).finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center text-gray-500">Cargando...</div>;
    if (!entry) return <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center text-gray-500">No encontrado</div>;

    const accent = entry.type === 'client' ? C.teal : C.orange;
    const backUrl = entry.type === 'client' ? '/catalogo/clientes' : '/catalogo/proveedores';

    return (
        <div className="min-h-screen bg-white dark:bg-[#0A0A0A] text-[#0A0A0A] dark:text-white">
            <div className="relative h-80 bg-gray-200 dark:bg-[#141414]">
                {entry.image_url && <img src={entry.image_url} alt={entry.name} className="w-full h-full object-cover" />}
                <Link to={backUrl} className="absolute top-6 left-6 inline-flex items-center gap-2 bg-white/90 dark:bg-black/70 backdrop-blur px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-700">
                    <ArrowLeft className="w-4 h-4" /> Volver al catálogo
                </Link>
            </div>
            <main className="max-w-[1200px] mx-auto px-6 -mt-16 relative pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-800 p-8">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border" style={{ color: accent, borderColor: accent }}>
                                {entry.industry}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 text-white" style={{ background: C.green }}>
                                <BadgeCheck className="w-3 h-3" /> AIGIS Certified
                            </span>
                        </div>
                        <h1 className="font-display text-3xl lg:text-5xl font-black tracking-tight mb-3">{entry.name}</h1>
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {entry.location}</span>
                            {entry.affiliated_year && <span>◆ Afiliado {entry.affiliated_year}</span>}
                        </div>
                        {entry.description && (
                            <p className="text-base leading-relaxed text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-800 pt-6">{entry.description}</p>
                        )}
                        {entry.certifications?.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
                                <h2 className="font-display text-lg font-bold mb-4 inline-flex items-center gap-2">
                                    <BadgeCheck className="w-5 h-5" style={{ color: C.green }} /> Distintivos Avalados
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {entry.certifications.map((c) => (
                                        <div key={c} className="border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm font-medium">{c}</div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="space-y-4">
                        {(entry.employees || entry.surface) && (
                            <div className="bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-800 p-6">
                                <div className="text-xs uppercase tracking-wider text-gray-500 mb-4">Métricas clave</div>
                                {entry.employees && <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800"><span>Personal</span><span className="font-bold">{entry.employees}</span></div>}
                                {entry.surface && <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800"><span>Superficie</span><span className="font-bold">{entry.surface}</span></div>}
                            </div>
                        )}
                        <div className="p-6 text-white" style={{ background: accent }}>
                            <div className="text-xs uppercase tracking-wider mb-4 opacity-80">Contacto oficial</div>
                            {entry.contact_phone && <div className="mb-2 text-sm">☎ {entry.contact_phone}</div>}
                            {entry.contact_email && <div className="mb-2 text-sm break-all">✉ {entry.contact_email}</div>}
                            {entry.website && <a href={entry.website} target="_blank" rel="noreferrer" className="text-sm underline break-all">↗ {entry.website}</a>}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
